import * as React from "react";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";

// -- Types ------------------------------------------------------------------

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectGroup {
  label: string;
  options: SelectOption[];
}

interface SelectProps {
  /** Flat list of options (mutually exclusive with `groups`) */
  options?: SelectOption[];
  /** Grouped options with category labels (mutually exclusive with `options`) */
  groups?: SelectGroup[];
  /** Currently selected value */
  value?: string;
  /** Callback when user selects an option */
  onChange?: (value: string) => void;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Error message -- turns trigger border red */
  error?: string;
  /** Help text shown below the trigger */
  helpText?: string;
  /** Label above the select */
  label?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Additional className for the root wrapper */
  className?: string;
}

// -- Helpers ----------------------------------------------------------------

function allOptions(
  options?: SelectOption[],
  groups?: SelectGroup[],
): SelectOption[] {
  if (options) return options;
  if (groups) return groups.flatMap((g) => g.options);
  return [];
}

function findLabel(
  value: string,
  options?: SelectOption[],
  groups?: SelectGroup[],
): string | undefined {
  return allOptions(options, groups).find((o) => o.value === value)?.label;
}

// -- Component --------------------------------------------------------------

const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options,
      groups,
      value,
      onChange,
      placeholder = "Select an option",
      error,
      helpText,
      label,
      disabled = false,
      className,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [highlightIndex, setHighlightIndex] = React.useState(-1);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const listboxRef = React.useRef<HTMLUListElement>(null);
    const generatedId = React.useId();
    const listboxId = `${generatedId}-listbox`;
    const labelId = `${generatedId}-label`;

    const flat = allOptions(options, groups);
    const enabledOptions = flat.filter((o) => !o.disabled);
    const selectedLabel = value ? findLabel(value, options, groups) : undefined;

    // -- Open / close --------------------------------------------------------

    const openDropdown = React.useCallback(() => {
      if (disabled) return;
      setOpen(true);
      const idx = value
        ? enabledOptions.findIndex((o) => o.value === value)
        : 0;
      setHighlightIndex(idx >= 0 ? idx : 0);
    }, [disabled, value, enabledOptions]);

    const closeDropdown = React.useCallback(() => {
      setOpen(false);
      setHighlightIndex(-1);
      triggerRef.current?.focus();
    }, []);

    const toggle = React.useCallback(() => {
      if (open) closeDropdown();
      else openDropdown();
    }, [open, openDropdown, closeDropdown]);

    // -- Selection -----------------------------------------------------------

    const selectOption = React.useCallback(
      (opt: SelectOption) => {
        if (opt.disabled) return;
        onChange?.(opt.value);
        closeDropdown();
      },
      [onChange, closeDropdown],
    );

    // -- Click-outside -------------------------------------------------------

    const rootRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      if (!open) return;
      function handleClickOutside(e: MouseEvent) {
        if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
          closeDropdown();
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, closeDropdown]);

    // -- Keyboard ------------------------------------------------------------

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        if (!open) {
          if (
            e.key === "ArrowDown" ||
            e.key === "ArrowUp" ||
            e.key === "Enter" ||
            e.key === " "
          ) {
            e.preventDefault();
            openDropdown();
          }
          return;
        }

        switch (e.key) {
          case "ArrowDown": {
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev < enabledOptions.length - 1 ? prev + 1 : 0,
            );
            break;
          }
          case "ArrowUp": {
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev > 0 ? prev - 1 : enabledOptions.length - 1,
            );
            break;
          }
          case "Enter":
          case " ": {
            e.preventDefault();
            if (highlightIndex >= 0 && highlightIndex < enabledOptions.length) {
              selectOption(enabledOptions[highlightIndex]);
            }
            break;
          }
          case "Escape": {
            e.preventDefault();
            closeDropdown();
            break;
          }
          case "Home": {
            e.preventDefault();
            setHighlightIndex(0);
            break;
          }
          case "End": {
            e.preventDefault();
            setHighlightIndex(enabledOptions.length - 1);
            break;
          }
        }
      },
      [open, openDropdown, closeDropdown, enabledOptions, highlightIndex, selectOption],
    );

    // -- Scroll highlighted option into view ---------------------------------

    React.useEffect(() => {
      if (!open || highlightIndex < 0) return;
      const listbox = listboxRef.current;
      if (!listbox) return;
      const highlighted = listbox.querySelector(
        `[data-highlight-index="${highlightIndex}"]`,
      );
      if (highlighted && typeof highlighted.scrollIntoView === "function") {
        highlighted.scrollIntoView({ block: "nearest" });
      }
    }, [open, highlightIndex]);

    // -- Render helpers ------------------------------------------------------

    function renderOption(opt: SelectOption, enabledIdx: number) {
      const isSelected = opt.value === value;
      const isHighlighted = enabledIdx === highlightIndex;

      return (
        <li
          key={opt.value}
          role="option"
          aria-selected={isSelected}
          aria-disabled={opt.disabled}
          data-highlight-index={enabledIdx}
          className={cn(
            "flex items-center justify-between px-2 py-1.5 text-sm cursor-pointer rounded-[4px]",
            isSelected && "bg-ev-green text-ev-text-primary",
            !isSelected && isHighlighted && "bg-ev-surface-subtle text-ev-text-primary",
            !isSelected && !isHighlighted && "text-ev-text-primary",
            opt.disabled && "opacity-50 cursor-not-allowed",
          )}
          onMouseEnter={() => {
            if (!opt.disabled) setHighlightIndex(enabledIdx);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            selectOption(opt);
          }}
        >
          <span>{opt.label}</span>
          {isSelected && (
            <Check className="h-4 w-4 text-ev-text-primary" aria-hidden="true" />
          )}
        </li>
      );
    }

    let enabledIdx = 0;

    function renderContent(): React.ReactNode {
      if (groups) {
        return groups.map((group) => (
          <li key={group.label} role="presentation">
            <div className="px-2 py-1.5 text-xs font-medium text-ev-text-secondary">
              {group.label}
            </div>
            <ul role="group" aria-label={group.label}>
              {group.options.map((opt) => {
                if (opt.disabled) {
                  return renderOption(opt, -1);
                }
                const idx = enabledIdx;
                enabledIdx++;
                return renderOption(opt, idx);
              })}
            </ul>
          </li>
        ));
      }
      if (options) {
        return options.map((opt) => {
          if (opt.disabled) {
            return renderOption(opt, -1);
          }
          const idx = enabledIdx;
          enabledIdx++;
          return renderOption(opt, idx);
        });
      }
      return null;
    }

    // Reset enabledIdx for each render
    enabledIdx = 0;

    return (
      <div
        ref={(node) => {
          // Merge forwarded ref with internal ref
          rootRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        className={cn("relative w-full", className)}
      >
        {label && (
          <label
            id={labelId}
            className="block text-sm font-medium text-ev-text-primary mb-1"
          >
            {label}
          </label>
        )}

        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          aria-labelledby={label ? labelId : undefined}
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between rounded-ev px-3 py-2 text-sm",
            "border transition-colors duration-ev",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ev-focus",
            error
              ? "border-ev-danger-text"
              : open
                ? "border-ev-border-strong"
                : "border-ev-border",
            disabled
              ? "opacity-50 cursor-not-allowed bg-ev-surface-subtle"
              : "bg-ev-surface",
          )}
          onClick={toggle}
          onKeyDown={handleKeyDown}
        >
          <span
            className={
              selectedLabel
                ? "text-ev-text-primary"
                : "text-ev-text-secondary"
            }
          >
            {selectedLabel ?? placeholder}
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-ev-text-secondary transition-transform duration-ev",
              open && "rotate-180",
            )}
            aria-hidden="true"
          />
        </button>

        {open && (
          <ul
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={label ? labelId : undefined}
            className="absolute z-50 mt-1 w-full overflow-auto rounded-ev border border-ev-border-strong bg-ev-surface p-1 shadow-lg max-h-60"
          >
            {renderContent()}
          </ul>
        )}

        {error && (
          <p className="text-ev-danger-text text-sm mt-1" role="alert">
            {error}
          </p>
        )}
        {!error && helpText && (
          <p className="text-ev-text-muted text-sm mt-1">{helpText}</p>
        )}
      </div>
    );
  },
);
Select.displayName = "Select";

export { Select };
